import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AddYardModal = ({ isOpen, onClose, onSaveYard, editingYard = null, saveError = null }) => {
  const isEditing = !!editingYard;

  const emptyForm = {
    name: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    region: '',
    capacity: '',
    manager: '',
    utilization_percent: '',
    status: 'active'
  };

  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Populate form when editing
  useEffect(() => {
    if (isOpen) {
      if (editingYard) {
        setFormData({
          name: editingYard?.name || '',
          address1: editingYard?.address1 || '',
          address2: editingYard?.address2 || '',
          city: editingYard?.city || '',
          state: editingYard?.state || '',
          zip: editingYard?.zip || '',
          region: editingYard?.region || '',
          capacity: editingYard?.capacity != null ? String(editingYard?.capacity) : '',
          manager: editingYard?.manager || '',
          utilization_percent: editingYard?.utilization != null ? String(editingYard?.utilization) : '',
          status: editingYard?.status || 'active'
        });
      } else {
        setFormData(emptyForm);
      }
      setErrors({});
    }
  }, [isOpen, editingYard]);

  const regionOptions = [
    { value: 'Northeast', label: 'Northeast' },
    { value: 'Southeast', label: 'Southeast' },
    { value: 'Midwest', label: 'Midwest' },
    { value: 'Southwest', label: 'Southwest' },
    { value: 'West', label: 'West' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'maintenance', label: 'Under Maintenance' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Yard name is required';
    }

    if (!formData?.address1?.trim()) {
      newErrors.address1 = 'Street address is required';
    }

    if (formData?.capacity && isNaN(parseInt(formData?.capacity))) {
      newErrors.capacity = 'Capacity must be a number';
    }

    if (formData?.utilization_percent && isNaN(parseInt(formData?.utilization_percent))) {
      newErrors.utilization_percent = 'Utilization must be a number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setIsSaving(true);
    await onSaveYard(formData);
    setIsSaving(false);
    // Parent controls whether to close (only on success)
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors?.[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[200]" onClick={onClose}></div>
      <div className="fixed inset-0 flex items-center justify-center z-[210] p-4">
        <div className="bg-card border border-border rounded-lg shadow-elevation-3 w-full max-w-2xl max-h-[90vh] overflow-hidden animate-slide-in">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                <Icon name={isEditing ? 'Edit2' : 'Plus'} size={20} color="var(--color-primary)" strokeWidth={2} />
              </div>
              <h2 className="text-lg font-semibold text-foreground">
                {isEditing ? 'Edit Yard' : 'Add New Yard'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="flex items-center justify-center w-8 h-8 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-md transition-hover"
            >
              <Icon name="X" size={20} strokeWidth={2} />
            </button>
          </div>

          {saveError && (
            <div className="mx-6 mt-4 p-3 bg-destructive/10 border border-destructive/30 rounded-md flex items-start space-x-2">
              <Icon name="AlertCircle" size={16} color="var(--color-destructive)" strokeWidth={2} className="mt-0.5 flex-shrink-0" />
              <p className="text-sm text-destructive">{saveError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="px-6 py-4 space-y-6">

              {/* Yard Name */}
              <Input
                label="Yard Name"
                type="text"
                placeholder="Main Distribution Center"
                value={formData?.name}
                onChange={(e) => handleChange('name', e?.target?.value)}
                error={errors?.name}
                required
              />

              {/* Address */}
              <div className="space-y-3">
                <Input
                  label="Street Address"
                  type="text"
                  placeholder="1250 Industrial Blvd"
                  value={formData?.address1}
                  onChange={(e) => handleChange('address1', e?.target?.value)}
                  error={errors?.address1}
                  required
                />
                <Input
                  label="Address Line 2 (optional)"
                  type="text"
                  placeholder="Suite 100"
                  value={formData?.address2}
                  onChange={(e) => handleChange('address2', e?.target?.value)}
                />
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    label="City"
                    type="text"
                    placeholder="Houston"
                    value={formData?.city}
                    onChange={(e) => handleChange('city', e?.target?.value)}
                  />
                  <Input
                    label="State"
                    type="text"
                    placeholder="TX"
                    value={formData?.state}
                    onChange={(e) => handleChange('state', e?.target?.value)}
                  />
                  <Input
                    label="ZIP"
                    type="text"
                    placeholder="77032"
                    value={formData?.zip}
                    onChange={(e) => handleChange('zip', e?.target?.value)}
                  />
                </div>
              </div>

              {/* Region, Capacity, Utilization */}
              <div className="grid grid-cols-3 gap-4">
                <Select
                  label="Region (optional)"
                  options={regionOptions}
                  value={formData?.region}
                  onChange={(value) => handleChange('region', value)}
                  placeholder="Select region..."
                />
                <Input
                  label="Capacity (optional)"
                  type="number"
                  placeholder="50"
                  value={formData?.capacity}
                  onChange={(e) => handleChange('capacity', e?.target?.value)}
                  error={errors?.capacity}
                />
                <Input
                  label="Utilization % (optional)"
                  type="number"
                  placeholder="75"
                  value={formData?.utilization_percent}
                  onChange={(e) => handleChange('utilization_percent', e?.target?.value)}
                  error={errors?.utilization_percent}
                />
              </div>

              {/* Manager */}
              <Input
                label="Manager Name (optional)"
                type="text"
                placeholder="John Smith"
                value={formData?.manager}
                onChange={(e) => handleChange('manager', e?.target?.value)}
              />

              {/* Status */}
              <Select
                label="Status"
                options={statusOptions}
                value={formData?.status}
                onChange={(value) => handleChange('status', value)}
                required
              />
            </div>

            <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-border bg-muted/30">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                iconName={isSaving ? 'Loader2' : (isEditing ? 'Check' : 'Plus')}
                iconPosition="left"
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Add Yard')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddYardModal;